import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  HelpCircle, Clock, Ghost, CheckCircle2, Building2, TrendingUp, Zap,
  ArrowRight, Menu, X, Facebook, Instagram, Linkedin, ChevronDown, Search,
  Coffee, Send,
} from "lucide-react";
import { LomooloLogo } from "@/components/ui/LomooloLogo";
import V3Footer from "@/components/layout/V3Footer";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ui/ScrollReveal";
import { motion } from "framer-motion";

/* ─── Shared helpers ─── */
const SectionLabel = ({ children }: { children: string }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[2px] text-v3-coral mb-4 font-dm">{children}</p>
);

const CoralButton = ({ children, to, large }: { children: React.ReactNode; to?: string; large?: boolean }) => {
  const cls = `inline-flex items-center gap-2 rounded-[10px] font-dm font-semibold text-[15px] text-white bg-v3-coral hover:bg-v3-coral-hover transition-all ${large ? "px-10 py-4" : "px-8 py-3.5"}`;
  return to ? <Link to={to} className={cls}>{children}</Link> : <button className={cls}>{children}</button>;
};

const OutlineButton = ({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) => (
  <button onClick={onClick} className="inline-flex items-center gap-2 rounded-[10px] font-dm font-semibold text-[15px] text-v3-heading border border-v3-heading/20 bg-transparent hover:bg-v3-heading/5 transition-all px-8 py-3.5">
    {children}
  </button>
);

/* ─── COMPONENT ─── */
const LandingPageV3 = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen font-dm text-v3-body overflow-x-hidden" role="document" style={{ background: "var(--color-v3-bg-primary)" }}>
      <SEO
        title="Lomoolo Social — Sociala medier på autopilot med AI"
        description="Lomoolo Social hjälper småföretag planera, skapa och publicera till Facebook, Instagram och LinkedIn. AI:n bygger din innehållsplan automatiskt."
        canonical="https://lomoolo.com/"
        ogTitle="Lomoolo Social — Sociala medier på autopilot med AI"
        ogDescription="Lomoolo Social hjälper småföretag planera, skapa och publicera till Facebook, Instagram och LinkedIn. AI:n bygger din innehållsplan automatiskt."
        ogUrl="https://lomoolo.com/"
        twitterTitle="Lomoolo Social — Sociala medier på autopilot med AI"
        twitterDescription="Lomoolo Social hjälper småföretag planera, skapa och publicera till Facebook, Instagram och LinkedIn. AI:n bygger din innehållsplan automatiskt."
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Lomoolo Social",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "description": "AI-drivet verktyg för sociala medier som hjälper svenska småföretag att planera, skapa och publicera innehåll till Facebook, Instagram och LinkedIn.",
            "url": "https://lomoolo.com",
            "offers": { "@type": "AggregateOffer", "priceCurrency": "SEK", "lowPrice": "599", "highPrice": "1199", "offerCount": "3" },
            "featureList": ["AI-genererad företagsprofil från domän", "Automatisk årsplanering för innehåll", "AI-assisterad innehållsskapande", "Publicering till Facebook, Instagram, LinkedIn", "Svensk högtids- och temadagskalender med 270+ dagar"],
            "provider": { "@type": "Organization", "name": "Lomoolo", "url": "https://lomoolo.com" }
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Lomoolo",
            "url": "https://lomoolo.com",
            "logo": "https://lomoolo.com/logo.png",
            "sameAs": ["https://www.facebook.com/lomoolo", "https://www.instagram.com/lomoolo", "https://www.linkedin.com/company/lomoolo"],
            "contactPoint": { "@type": "ContactPoint", "email": "hej@lomoolo.com", "contactType": "customer service", "availableLanguage": ["Swedish", "English"] }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "Vad är Lomoolo Social?", "acceptedAnswer": { "@type": "Answer", "text": "Lomoolo Social är ett AI-drivet verktyg för sociala medier som hjälper svenska småföretag att automatiskt planera, skapa och publicera innehåll till Facebook, Instagram och LinkedIn." } },
              { "@type": "Question", "name": "Behöver jag betalkort för att testa?", "acceptedAnswer": { "@type": "Answer", "text": "Nej. Du testar gratis i 14 dagar utan att ange betaluppgifter." } },
              { "@type": "Question", "name": "Vad kostar Lomoolo Social?", "acceptedAnswer": { "@type": "Answer", "text": "Lomoolo Social erbjuder tre prisplaner: Starter (599 kr/mån), Growth (899 kr/mån) och Pro (1 199 kr/mån). Alla planer inkluderar 14 dagars gratis provperiod och årsbetalning ger 2 månader gratis." } },
              { "@type": "Question", "name": "Vilka sociala medier stöds?", "acceptedAnswer": { "@type": "Answer", "text": "Lomoolo Social stödjer publicering till Facebook, Instagram och LinkedIn. Fler plattformar planeras." } },
              { "@type": "Question", "name": "Hur fungerar AI-texterna?", "acceptedAnswer": { "@type": "Answer", "text": "AI:n skriver ett första utkast baserat på ditt ämne, din bransch och din tonalitet. Du redigerar och gör det till ditt." } },
              { "@type": "Question", "name": "Passar Lomoolo Social min bransch?", "acceptedAnswer": { "@type": "Answer", "text": "Ja. AI:n analyserar din webbplats och anpassar ämnesförslag till just din bransch — oavsett om du driver kafé, redovisningsbyrå eller e-handel." } },
              { "@type": "Question", "name": "Kan jag avbryta när som helst?", "acceptedAnswer": { "@type": "Answer", "text": "Ja. Inga bindningstider. Avsluta din plan med ett klick." } }
            ]
          }
        ]}
      />

      {/* ── 00 NAVBAR ── */}
      <nav aria-label="Huvudnavigering" className="sticky top-0 z-50 bg-white/95 backdrop-blur shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        <div className="max-w-7xl mx-auto flex h-16 items-center justify-between px-5">
          <Link to="/" className="flex items-center gap-2.5">
            <LomooloLogo size="sm" />
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo("funktioner")} className="text-sm font-medium text-v3-body hover:text-v3-heading transition-colors">Funktioner</button>
            <button onClick={() => scrollTo("sa-funkar-det")} className="text-sm font-medium text-v3-body hover:text-v3-heading transition-colors">Så funkar det</button>
            <button onClick={() => scrollTo("priser")} className="text-sm font-medium text-v3-body hover:text-v3-heading transition-colors">Priser</button>
            <Link to="/blogg" className="text-sm font-medium text-v3-body hover:text-v3-heading transition-colors">Blogg</Link>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <Link to="/login" className="text-sm font-medium text-v3-body hover:text-v3-heading transition-colors">Logga in</Link>
            <CoralButton to="/signup">Testa gratis <ArrowRight className="h-4 w-4" /></CoralButton>
          </div>

          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? "Stäng meny" : "Öppna meny"} aria-expanded={mobileMenuOpen}>
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-white px-5 py-4 space-y-3">
            <button onClick={() => scrollTo("funktioner")} className="block w-full text-left text-sm font-medium py-2">Funktioner</button>
            <button onClick={() => scrollTo("sa-funkar-det")} className="block w-full text-left text-sm font-medium py-2">Så funkar det</button>
            <button onClick={() => scrollTo("priser")} className="block w-full text-left text-sm font-medium py-2">Priser</button>
            <Link to="/blogg" className="block text-sm font-medium py-2">Blogg</Link>
            <Link to="/login" className="block text-sm font-medium py-2">Logga in</Link>
            <CoralButton to="/signup">Testa gratis <ArrowRight className="h-4 w-4" /></CoralButton>
          </div>
        )}
      </nav>

      {/* ── 01 HERO ── */}
      <main>
      <section className="pt-20 pb-8 md:pt-28 md:pb-12 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-v3-teal/20 bg-v3-teal/10 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-v3-teal" />
            <span className="text-xs font-medium text-v3-teal">AI-driven innehållsplanering för småföretag</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="font-jakarta font-bold text-[28px] sm:text-[36px] md:text-[48px] leading-[1.2] text-v3-heading mb-6"
          >
            Du sköter företaget.<br />
            <span className="text-v3-coral">LOMOOLO</span> sköter inläggen.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-base text-v3-body leading-[1.7] max-w-[520px] mx-auto mb-8"
          >
            Skriv in din domän. Få en skräddarsydd årsplan för sociala medier — med ämnesförslag, AI-skriven text och automatisk publicering. På en eftermiddag.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4"
          >
            <CoralButton to="/signup">Testa gratis i 14 dagar <ArrowRight className="h-4 w-4" /></CoralButton>
            <OutlineButton onClick={() => scrollTo("sa-funkar-det")}>Se hur det funkar</OutlineButton>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-xs text-v3-light"
          >
            Inget betalkort krävs. Kom igång på 2 minuter.
          </motion.p>
        </div>

        {/* Browser mockup */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          className="max-w-4xl mx-auto mt-12 -mb-16 relative z-10"
        >
          <div className="rounded-xl overflow-hidden shadow-2xl border border-v3-border">
            <div className="bg-v3-dark px-4 py-2.5 flex items-center gap-2">
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" />
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-white/10 rounded-md px-3 py-1 text-xs text-white/60 text-center">app.lomoolo.com</div>
              </div>
            </div>
            <div className="bg-white p-4 md:p-6">
              <YearPlanMockupV3 />
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── 02 SOCIAL PROOF / SIFFROR ── */}
      <section className="bg-v3-dark pt-24 pb-12 md:pb-16 px-5">
        <ScrollReveal variant="fade-in">
          <p className="text-center text-[11px] uppercase tracking-[2px] font-semibold text-white/50 mb-6">Redan igång</p>
        </ScrollReveal>
        <StaggerContainer className="flex justify-center gap-12 md:gap-20 flex-wrap" staggerDelay={0.15}>
          {[
            { num: "270+", label: "Svenska temadagar\ninbyggda" },
            { num: "52", label: "Veckors innehåll\nplanerat" },
            { num: "45", label: "Minuter till en\nhel årsplan" },
          ].map((s) => (
            <StaggerItem key={s.num}>
              <div className="text-center">
                <div className="font-jakarta font-bold text-[42px] text-v3-teal">{s.num}</div>
                <div className="text-[13px] text-white/70 whitespace-pre-line mt-1">{s.label}</div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </section>

      {/* ── 03 PAIN POINTS ── */}
      <section className="bg-white py-20 md:py-28 px-5">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <SectionLabel>Känner du igen dig?</SectionLabel>
            <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading mb-3">Ambitionen finns. Tiden gör det inte.</h2>
            <p className="text-[15px] text-v3-body mb-12 max-w-xl">De flesta småföretag vet att de borde synas online. Men verkligheten ser annorlunda ut.</p>
          </ScrollReveal>

          <StaggerContainer className="grid md:grid-cols-3 gap-5" staggerDelay={0.12}>
            {[
              { icon: HelpCircle, title: "\"Vad ska jag skriva om?\"", text: "Du vill posta varje vecka men vet aldrig vad. Inspirationen tar slut och idéerna känns uttjatade." },
              { icon: Clock, title: "Stressen när det är dags", text: "Inget förberett, allt annat prioriteras. Tiden för innehåll finns aldrig och det blir sällan gjort." },
              { icon: Ghost, title: "Det övergivna kontot", text: "Senaste inlägget? Sex månader sedan. Du vet att det ser dåligt ut men orkar inte ta tag i det." },
            ].map((c) => (
              <StaggerItem key={c.title}>
                <div className="bg-v3-card rounded-xl p-6">
                  <c.icon className="h-7 w-7 text-v3-teal mb-4" />
                  <h3 className="font-jakarta font-bold text-base text-v3-heading mb-2">{c.title}</h3>
                  <p className="text-sm text-v3-body leading-relaxed">{c.text}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <ScrollReveal delay={0.3}>
            <p className="text-center text-[15px] font-medium text-v3-body mt-10">Det är därför vi byggde LOMOOLO.</p>
          </ScrollReveal>
        </div>
      </section>

      {/* ── 04 SÅ FUNKAR DET ── */}
      <section id="sa-funkar-det" className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
        <div className="max-w-5xl mx-auto text-center">
          <ScrollReveal>
            <SectionLabel>Så funkar det</SectionLabel>
            <h2 className="font-jakarta font-bold text-[28px] md:text-[34px] text-v3-heading mb-3">Fyra steg. En eftermiddag. Ett helt år.</h2>
            <p className="text-[15px] text-v3-body mb-12 max-w-lg mx-auto">Från tom kalender till publicerat innehåll — utan att det tar över din vardag.</p>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4" staggerDelay={0.12}>
            {[
              { n: 1, name: "Grunda", text: "Skriv in din domän. LOMOOLO analyserar ditt företag, din bransch och din målgrupp automatiskt.", color: "bg-v3-coral" },
              { n: 2, name: "Planera", text: "Få en komplett årsplan med 52 veckors ämnesförslag — anpassade för just ditt företag.", color: "bg-v3-teal" },
              { n: 3, name: "Skapa", text: "Låt AI skriva utkast och generera bilder. Justera med din egen röst på några minuter.", color: "bg-v3-coral" },
              { n: 4, name: "Publicera", text: "Schemalägg och publicera automatiskt. LOMOOLO sköter resten.", color: "bg-v3-teal" },
            ].map((s, i) => (
              <StaggerItem key={s.n}>
                <div className="relative text-center">
                  <div className={`w-12 h-12 ${s.color} rounded-full flex items-center justify-center text-white font-jakarta font-bold text-lg mx-auto mb-4`}>
                    {s.n}
                  </div>
                  {i < 3 && <div className="hidden md:block absolute top-6 left-[calc(50%+30px)] w-[calc(100%-60px)] border-t-2 border-dashed border-v3-border" />}
                  <h3 className="font-jakarta font-bold text-base text-v3-heading mb-2">{s.name}</h3>
                  <p className="text-[13px] text-v3-body leading-relaxed max-w-[220px] mx-auto">{s.text}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── 05 GRUNDA ── */}
      <section id="funktioner" className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <ScrollReveal variant="slide-left">
            <SectionLabel>Grunda</SectionLabel>
            <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading mb-4">Skriv in din domän. LOMOOLO förstår resten.</h2>
            <p className="text-sm text-v3-body leading-[1.7]">
              Inget formulär med 20 frågor. Skriv in din webbadress så analyserar AI:n ditt företag, dina produkter, din målgrupp och din tonalitet. På sekunder har LOMOOLO en komplett bild av vem du är — och vad du borde posta.
            </p>
          </ScrollReveal>
          <ScrollReveal variant="slide-right" delay={0.2}>
            <DomainMockup />
          </ScrollReveal>
        </div>
      </section>

      {/* ── 06 PLANERA ── */}
      <section className="bg-white py-20 md:py-28 px-5">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <ScrollReveal variant="slide-left" delay={0.1}>
            <WeekPlanMockup />
          </ScrollReveal>
          <ScrollReveal variant="slide-right">
            <SectionLabel>Planera</SectionLabel>
            <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading mb-4">52 veckor. Planerade på en eftermiddag.</h2>
            <p className="text-sm text-v3-body leading-[1.7] mb-4">
              Medan andra verktyg ger dig en tom kalender att fylla, genererar LOMOOLO en komplett årsplan med ämnesidéer anpassade för din bransch. Kanelbullens dag, Internationella kaffedagen, skattetips i januari — allt på plats automatiskt.
            </p>
            <div className="flex items-center gap-2 text-sm text-v3-teal font-medium">
              <CheckCircle2 className="h-4 w-4" /> 270+ svenska högtider och temadagar inbyggda
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── 07 SKAPA ── */}
      <section className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <ScrollReveal variant="slide-left">
            <SectionLabel>Skapa</SectionLabel>
            <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading mb-4">AI skriver. Du gör det till ditt.</h2>
            <p className="text-sm text-v3-body leading-[1.7]">
              Välj ett ämne och låt LOMOOLO skriva ett första utkast — anpassat för varje kanal. Kortare för Instagram, professionellt för LinkedIn. Redigera, justera tonen och gör det till ditt eget på några minuter. Generera matchande bilder med AI.
            </p>
          </ScrollReveal>
          <ScrollReveal variant="slide-right" delay={0.2}>
            <AIWriteMockup />
          </ScrollReveal>
        </div>
      </section>

      {/* ── 08 PUBLICERA ── */}
      <section className="bg-white py-20 md:py-28 px-5">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <ScrollReveal variant="slide-left" delay={0.1}>
            <PublishMockup />
          </ScrollReveal>
          <ScrollReveal variant="slide-right">
            <SectionLabel>Publicera</SectionLabel>
            <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading mb-4">Schemalägg. Luta dig tillbaka. Klart.</h2>
            <p className="text-sm text-v3-body leading-[1.7] mb-4">
              Koppla dina sociala konton, välj tid och datum — LOMOOLO publicerar automatiskt. Se vad som är schemalagt, vad som publicerats och fånga upp eventuella fel. Full överblick utan att logga in på varje plattform.
            </p>
            <div className="flex items-center gap-4 text-sm text-v3-body">
              <Facebook className="h-5 w-5 text-[#1877F2]" aria-hidden="true" />
              <Instagram className="h-5 w-5 text-[#E4405F]" aria-hidden="true" />
              <Linkedin className="h-5 w-5 text-[#0A66C2]" aria-hidden="true" />
              <span>Publicera direkt till Facebook, Instagram och LinkedIn</span>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── 09 JÄMFÖRELSE ── */}
      <section className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
        <div className="max-w-4xl mx-auto">
          <ScrollReveal>
            <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading text-center mb-3">Mer än en schemaläggare</h2>
            <p className="text-[15px] text-v3-body text-center mb-10 max-w-lg mx-auto">LOMOOLO gör det som schemaläggare inte kan — hjälper dig veta vad du ska posta, inte bara när.</p>
          </ScrollReveal>

          <ScrollReveal variant="scale-in" delay={0.1}>
            <div className="overflow-x-auto mb-16">
              <table className="w-full text-sm" role="table">
                <thead>
                  <tr>
                    <th scope="col" className="text-left py-3 px-4 font-medium text-v3-light">Funktion</th>
                    <th scope="col" className="text-center py-3 px-4 font-medium text-v3-light">Vanliga verktyg</th>
                    <th scope="col" className="text-center py-3 px-4 font-bold text-v3-heading bg-v3-teal/5 rounded-t-lg">LOMOOLO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-v3-border-light">
                  {[
                    { f: "Schemalägga inlägg", o: true, l: true },
                    { f: "Publicera automatiskt", o: true, l: true },
                    { f: "AI-skriven text", o: "Vissa", l: true },
                    { f: "AI-genererade bilder", o: false, l: true },
                    { f: "Ämnesförslag per bransch", o: false, l: true },
                    { f: "Komplett årsplan", o: false, l: true },
                    { f: "Svenska högtider & temadagar", o: false, l: true },
                    { f: "Helt på svenska", o: false, l: true },
                  ].map((r) => (
                    <tr key={r.f}>
                      <td className="py-3 px-4 text-v3-heading">{r.f}</td>
                      <td className="py-3 px-4 text-center">
                        {r.o === true ? <><CheckCircle2 className="h-5 w-5 text-v3-teal mx-auto" aria-hidden="true" /><span className="sr-only">Ja</span></> : r.o ? <span className="text-v3-light text-xs">{r.o}</span> : <><X className="h-5 w-5 text-v3-border mx-auto" aria-hidden="true" /><span className="sr-only">Nej</span></>}
                      </td>
                      <td className="py-3 px-4 text-center bg-v3-teal/5">
                        <><CheckCircle2 className="h-5 w-5 text-v3-teal mx-auto" aria-hidden="true" /><span className="sr-only">Ja</span></>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ScrollReveal>

          {/* B — Prisjämförelse */}
          <ScrollReveal>
            <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading text-center mb-8">Professionell marknadsföring — utan byråprislappen</h2>
          </ScrollReveal>
          <StaggerContainer className="max-w-2xl mx-auto space-y-3" staggerDelay={0.1}>
            <StaggerItem>
              <div className="flex items-center justify-between p-5 rounded-xl bg-white border">
                <span className="text-v3-heading">Social media-byrå</span>
                <span className="text-v3-body font-medium">8 000 – 15 000 kr/mån</span>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="flex items-center justify-between p-5 rounded-xl bg-white border">
                <span className="text-v3-heading">Frilansande innehållsskapare</span>
                <span className="text-v3-body font-medium">3 000 – 8 000 kr/mån</span>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="flex items-center justify-between p-5 rounded-xl bg-v3-coral/10 border border-v3-coral/30">
                <div className="flex items-center gap-3">
                  <span className="text-v3-heading font-semibold">LOMOOLO</span>
                  <Facebook className="h-4 w-4 text-[#1877F2]" />
                  <Instagram className="h-4 w-4 text-[#E4405F]" />
                  <Linkedin className="h-4 w-4 text-[#0A66C2]" />
                </div>
                <span className="text-v3-coral font-bold text-lg">från 599 kr/mån</span>
              </div>
            </StaggerItem>
          </StaggerContainer>
          <ScrollReveal delay={0.3}>
            <p className="text-center text-v3-body text-[15px] mt-6">Samma resultat. En bråkdel av kostnaden. Och du behåller kontrollen.</p>
            <div className="text-center mt-8">
              <CoralButton to="/signup">Testa gratis i 14 dagar <ArrowRight className="h-4 w-4" /></CoralButton>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── 10 CASE STUDY ── */}
      <section className="bg-v3-dark py-20 md:py-28 px-5">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <p className="text-[11px] uppercase tracking-[2px] font-semibold text-white/50 mb-4">Från tom kalender till ett helt år</p>
            <h2 className="font-jakarta font-bold text-[26px] md:text-[30px] text-white mb-6">Anna planerade ett helt år på 45 minuter.</h2>
            <p className="text-[15px] text-white/75 leading-[1.7] mb-10 max-w-[640px]">
              Anna driver ett kafé i Göteborg. Hon visste att hon borde posta på Instagram, men kom aldrig på vad. Med LOMOOLO fyllde hon en hel årsplan med ämnesidéer — anpassade för hennes bransch. Kanelbullens dag, Internationella kaffedagen, "Bakom kulisserna med vår barista." Sen låter hon AI:n skriva ett första utkast för varje inlägg, justerar med sin egen röst, och schemalägger.
            </p>
          </ScrollReveal>
          <StaggerContainer className="flex gap-10 md:gap-16 flex-wrap" staggerDelay={0.15}>
            {[
              { num: "45 min", sub: "att planera\nett helt år" },
              { num: "1 tim", sub: "per månad för\nallt innehåll" },
              { num: "0", sub: "missade veckor" },
            ].map((s) => (
              <StaggerItem key={s.num}>
                <div className="text-center">
                  <div className="font-jakarta font-bold text-[36px] text-v3-coral">{s.num}</div>
                  <div className="text-xs text-white/50 whitespace-pre-line mt-1">{s.sub}</div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── 11 VEM ÄR LOMOOLO FÖR ── */}
      <section className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <SectionLabel>Vem är LOMOOLO för?</SectionLabel>
            <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading mb-3">Du som har bättre saker att göra.</h2>
            <p className="text-[15px] text-v3-body mb-12">LOMOOLO är byggt för dig som vill synas — utan att det tar över din vardag.</p>
          </ScrollReveal>

          <StaggerContainer className="grid md:grid-cols-3 gap-5" staggerDelay={0.12}>
            {[
              { icon: Building2, title: "Småföretagaren", text: "Du vet att synlighet online är viktigt, men det finns alltid annat som prioriteras före marknadsföring. LOMOOLO gör det till den enklaste delen av din vecka." },
              { icon: TrendingUp, title: "Den ambitiösa", text: "Du saknar redaktionellt stöd och har inte råd med en byrå — men vill ändå ha professionellt innehåll. LOMOOLO ger dig en hel content-avdelning i en app." },
              { icon: Zap, title: "Tidspressaren", text: "Du vill bygga ett starkt varumärke online utan att det tar över din vardag. LOMOOLO ger dig tillbaka den tid du inte visste att du hade." },
            ].map((c) => (
              <StaggerItem key={c.title}>
                <div className="bg-v3-card rounded-xl p-6 text-center">
                  <c.icon className="h-7 w-7 text-v3-teal mx-auto mb-4" />
                  <h3 className="font-jakarta font-bold text-base text-v3-heading mb-2">{c.title}</h3>
                  <p className="text-sm text-v3-body leading-relaxed">{c.text}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── 12 PRISER ── */}
      <section id="priser" className="bg-white py-20 md:py-28 px-5">
        <div className="max-w-5xl mx-auto text-center">
          <ScrollReveal>
            <SectionLabel>Priser</SectionLabel>
            <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading mb-3">Välj den plan som passar dig.</h2>
            <p className="text-[15px] text-v3-body mb-12">Alla planer inkluderar 14 dagars gratis provperiod. Inget betalkort krävs.</p>
          </ScrollReveal>

          <StaggerContainer className="grid md:grid-cols-3 gap-5 items-start" staggerDelay={0.12}>
            {/* Starter */}
            <StaggerItem>
              <div className="rounded-2xl border border-v3-border bg-white p-8 text-left">
                <h3 className="font-jakarta font-bold text-xl text-v3-heading mb-1">Starter</h3>
                <div className="mb-1"><span className="font-jakarta font-bold text-4xl text-v3-coral">599</span> <span className="text-sm text-v3-light">kr/mån</span></div>
                <p className="text-sm text-v3-body mb-6">Perfekt för att komma igång.</p>
                <ul className="space-y-3 mb-8 text-sm text-v3-body">
                  {["AI-analys av ditt företag", "Årsplan med ämnesförslag", "1 inlägg/vecka per kanal", "1 social kanal", "AI-skriven text"].map((f) => (
                    <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-v3-teal mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Link to="/signup" className="block w-full text-center rounded-[10px] border border-v3-heading/20 py-3 text-sm font-semibold text-v3-heading hover:bg-v3-heading/5 transition-colors">
                  Testa gratis i 14 dagar
                </Link>
              </div>
            </StaggerItem>

            {/* Growth */}
            <StaggerItem>
              <div className="rounded-2xl bg-v3-dark p-8 text-left text-white relative md:scale-[1.03] shadow-xl">
                <span className="absolute -top-3 right-6 bg-v3-coral text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">Populärast</span>
                <h3 className="font-jakarta font-bold text-xl mb-1">Growth</h3>
                <div className="mb-1"><span className="font-jakarta font-bold text-4xl text-v3-coral">899</span> <span className="text-sm text-white/50">kr/mån</span></div>
                <p className="text-sm text-white/60 mb-6">För dig som vill synas mer.</p>
                <ul className="space-y-3 mb-8 text-sm text-white/70">
                  {["Allt i Starter", "2 inlägg/vecka per kanal", "2 sociala kanaler", "AI-bildgenerering", "270+ svenska temadagar"].map((f) => (
                    <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-v3-teal mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Link to="/signup" className="block w-full text-center rounded-[10px] bg-v3-coral hover:bg-v3-coral-hover py-3 text-sm font-semibold text-white transition-colors">
                  Testa gratis i 14 dagar <span className="ml-1">→</span>
                </Link>
              </div>
            </StaggerItem>

            {/* Pro */}
            <StaggerItem>
              <div className="rounded-2xl border border-v3-border bg-white p-8 text-left">
                <h3 className="font-jakarta font-bold text-xl text-v3-heading mb-1">Pro</h3>
                <div className="mb-1"><span className="font-jakarta font-bold text-4xl text-v3-coral">1 199</span> <span className="text-sm text-v3-light">kr/mån</span></div>
                <p className="text-sm text-v3-body mb-6">Allt du behöver för att dominera.</p>
                <ul className="space-y-3 mb-8 text-sm text-v3-body">
                  {["Allt i Growth", "3 inlägg/vecka per kanal", "3 sociala kanaler", "Automatisk publicering", "Prioriterad support"].map((f) => (
                    <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-v3-teal mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Link to="/signup" className="block w-full text-center rounded-[10px] border border-v3-heading/20 py-3 text-sm font-semibold text-v3-heading hover:bg-v3-heading/5 transition-colors">
                  Testa gratis i 14 dagar
                </Link>
              </div>
            </StaggerItem>
          </StaggerContainer>

          <ScrollReveal delay={0.3}>
            <p className="text-[13px] text-v3-body mt-8">💡 Spara 2 månader med årsbetalning.</p>
          </ScrollReveal>
        </div>
      </section>

      {/* ── 13 FAQ ── */}
      <section className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
        <div className="max-w-2xl mx-auto">
          <ScrollReveal>
            <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading text-center mb-10">Vanliga frågor</h2>
          </ScrollReveal>
          <ScrollReveal variant="fade-up" delay={0.1}>
            <Accordion type="single" collapsible className="space-y-0 divide-y">
              {[
                { q: "Behöver jag betalkort för att testa?", a: "Nej. Du testar gratis i 14 dagar utan att ange betaluppgifter." },
                { q: "Kan jag byta plan senare?", a: "Absolut. Du kan uppgradera eller nedgradera när som helst." },
                { q: "Vilka sociala medier stöds?", a: "Facebook, Instagram och LinkedIn. Fler plattformar är på väg." },
                { q: "Hur fungerar AI-texterna?", a: "AI:n skriver ett första utkast baserat på ditt ämne, din bransch och din tonalitet. Du redigerar och gör det till ditt." },
                { q: "Passar LOMOOLO min bransch?", a: "Ja. AI:n analyserar din webbplats och anpassar ämnesförslag till just din bransch — oavsett om du driver kafé, redovisningsbyrå eller e-handel." },
                { q: "Kan jag avbryta när som helst?", a: "Ja. Inga bindningstider. Avsluta din plan med ett klick." },
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-0">
                  <AccordionTrigger className="font-jakarta font-semibold text-base text-v3-heading py-5 hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-v3-body pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </ScrollReveal>
        </div>
      </section>

      {/* ── 14 FINAL CTA ── */}
      <section className="bg-v3-dark py-20 md:py-28 px-5">
        <div className="max-w-2xl mx-auto text-center">
          <ScrollReveal>
            <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-white mb-4">Redo att ta kontroll över ditt innehåll?</h2>
            <p className="text-[15px] text-white/65 mb-8 max-w-md mx-auto">Skriv in din domän. Få en hel årsplan. Publicera utan att tänka på det. Det tar 2 minuter att komma igång.</p>
            <CoralButton to="/signup" large>Testa gratis i 14 dagar <ArrowRight className="h-4 w-4" /></CoralButton>
            <p className="text-xs text-white/40 mt-4">Inget betalkort krävs. Avbryt när som helst.</p>
          </ScrollReveal>
        </div>
      </section>

      </main>
      {/* ── 15 FOOTER ── */}
      <V3Footer />
    </div>
  );
};

/* ─── MOCKUPS ─── */

function YearPlanMockupV3() {
  const weeks = [
    { week: "V.1", date: "2 jan", fb: "Nyårslöften för företagare", ig: "Tips: 5 sätt att starta starkt 2026", status: "Planerad" },
    { week: "V.2", date: "9 jan", fb: "Vinter-special: varm choklad", ig: "Behind the scenes: morgonrutin", status: "Planerad" },
    { week: "V.3", date: "16 jan", fb: "Skattetips inför deklaration", ig: "Kundens favorit just nu", status: "Skapad" },
    { week: "V.4", date: "23 jan", fb: "Våffeldagen närmar sig!", ig: "Recept: klassiska våfflor", status: "Skapad" },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs md:text-sm">
        <thead>
          <tr className="text-left text-v3-light">
            <th className="py-2 px-3 font-medium">Vecka</th>
            <th className="py-2 px-3 font-medium">Datum</th>
            <th className="py-2 px-3 font-medium"><span className="inline-flex items-center gap-1"><Facebook className="h-3.5 w-3.5 text-[#1877F2]" /> Facebook</span></th>
            <th className="py-2 px-3 font-medium"><span className="inline-flex items-center gap-1"><Instagram className="h-3.5 w-3.5 text-[#E4405F]" /> Instagram</span></th>
            <th className="py-2 px-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.week} className="border-t border-v3-border-light">
              <td className="py-3 px-3 font-medium text-v3-heading">{w.week}</td>
              <td className="py-3 px-3 text-v3-body">{w.date}</td>
              <td className="py-3 px-3 text-v3-heading">{w.fb}</td>
              <td className="py-3 px-3 text-v3-heading">{w.ig}</td>
              <td className="py-3 px-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${w.status === "Planerad" ? "bg-v3-muted text-v3-light" : "bg-v3-teal/10 text-v3-teal"}`}>
                  {w.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DomainMockup() {
  const results = [
    { key: "Bransch", value: "Kafé & restaurang" },
    { key: "Målgrupp", value: "Lokala kaffeälskare, 25–55" },
    { key: "Tonalitet", value: "Varm, personlig, inbjudande" },
    { key: "Ämnesförslag", value: "52 veckor genererade" },
  ];
  return (
    <div className="bg-v3-card rounded-2xl p-8">
      <div className="bg-white rounded-xl px-4 py-3 flex items-center gap-3 mb-5 shadow-sm">
        <div className="w-9 h-9 rounded-full bg-v3-teal flex items-center justify-center">
          <Search className="h-4 w-4 text-white" />
        </div>
        <span className="text-sm font-medium text-v3-heading">mittlillakafe.se</span>
      </div>
      <div className="space-y-2.5">
        {results.map((r) => (
          <div key={r.key} className="bg-white rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm">
            <div className="w-7 h-7 rounded-full bg-v3-teal/10 flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 text-v3-teal" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-v3-light font-semibold">{r.key}</div>
              <div className="text-sm font-semibold text-v3-heading">{r.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WeekPlanMockup() {
  const weeks = [
    { num: "V.10", topic: "Bakom kulisserna: vår barista", done: true },
    { num: "V.11", topic: "Våffeldagen — recept & erbjudande", done: true },
    { num: "V.12", topic: "Vårens nya meny avslöjas", done: false },
    { num: "V.13", topic: "Påskspecial: bakverksguide", done: false },
    { num: "V.14", topic: "Internationella kaffedagen", done: false },
  ];
  return (
    <div className="bg-v3-card rounded-2xl p-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-jakarta font-bold text-base text-v3-heading">Veckoplan</h3>
        <span className="text-xs text-v3-light">Mars 2026</span>
      </div>
      <div className="space-y-2">
        {weeks.map((w) => (
          <div key={w.num} className="bg-white rounded-xl px-4 py-3 flex items-center gap-3">
            <span className={`text-xs font-bold ${w.done ? "text-v3-teal" : "text-v3-coral"}`}>{w.num}</span>
            <span className="text-sm text-v3-heading flex-1">{w.topic}</span>
            {w.done ? (
              <CheckCircle2 className="h-5 w-5 text-v3-teal" />
            ) : (
              <ArrowRight className="h-4 w-4 text-v3-coral" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AIWriteMockup() {
  return (
    <div className="bg-v3-card rounded-2xl p-8">
      <div className="bg-white rounded-xl px-4 py-3 mb-4">
        <div className="text-[10px] uppercase tracking-wider text-v3-light font-semibold mb-1">Valt ämne</div>
        <div className="flex items-center gap-2">
          <Coffee className="h-4 w-4 text-v3-coral" />
          <span className="text-sm font-semibold text-v3-heading">Internationella kaffedagen</span>
        </div>
      </div>
      <div className="bg-white rounded-xl px-4 py-4 border-l-4 border-v3-teal">
        <div className="text-[10px] uppercase tracking-wider text-v3-teal font-semibold mb-2">LOMOOLO föreslår</div>
        <p className="text-sm text-v3-heading leading-relaxed">
          Visste du att vi provsmakar minst 12 sorter innan vi väljer vårt kaffe? Idag firar vi Internationella kaffedagen med 20% på alla espressodrinkar. Kom förbi och upptäck din nya favorit!
        </p>
      </div>
    </div>
  );
}

function PublishMockup() {
  const items = [
    { platform: "IG", color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]", title: "Internationella kaffedagen", time: "Imorgon 08:30", status: "Schemalagd", statusColor: "bg-v3-teal/10 text-v3-teal" },
    { platform: "FB", color: "bg-[#1877F2]", title: "Bakom kulisserna: vår barista", time: "Igår 09:00", status: "Publicerad", statusColor: "bg-v3-success-light text-v3-success" },
    { platform: "IN", color: "bg-[#0A66C2]", title: "Hållbart kaffe: vår resa", time: "Måndag 10:00", status: "Schemalagd", statusColor: "bg-v3-teal/10 text-v3-teal" },
  ];
  return (
    <div className="bg-v3-card rounded-2xl p-8 space-y-3">
      {items.map((item) => (
        <div key={item.title} className="bg-white rounded-xl px-4 py-3 flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
            {item.platform}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-v3-heading truncate">{item.title}</div>
            <div className="text-xs text-v3-light">{item.time}</div>
          </div>
          <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shrink-0 ${item.statusColor}`}>
            {item.status}
          </span>
        </div>
      ))}
    </div>
  );
}

export default LandingPageV3;
